import type {
  IAnswer,
  IDefinitionSnapshot,
  IFinding,
  IRenderState,
  IRepeatRow,
  ISnapshotOption,
  ISnapshotSection,
  ISnapshotUsage
} from '../api/contracts';
import { CONTAINER_FIELD_TYPES, isCellsField } from './answer-values';
import { formatMessage } from './field-constraints';
// `prefill-view` imports this module too; `sourceCopy` is read when a finding is said, never while
// either module loads, so the cycle never meets a binding not yet set.
import { sourceCopy } from './prefill-view';

/**
 * Turns survey-api's flat definition snapshot into the tree the form renders.
 *
 * The snapshot is deliberately flat — sections in ordinal order, usages carrying
 * a section code plus a `parentUsage`/`childRole` link. Three shapes come out of
 * folding it, and they are the only nesting the renderer knows:
 *
 * - **section → page.** There are no pages in the model, so each section is one
 *   step of the stepper. Section ordinal is the order.
 * - **child usages** (`sub_question`, `per_option_sub`) render under their
 *   parent. Their visibility is not this app's to derive: a per-option sub is
 *   revealed by the engine, which already reports it through `usageVisibility`.
 * - **repeat members** render once per active repeat row, addressed by that
 *   row's `instanceId`.
 */

export interface IUsageNode {
  usage: ISnapshotUsage;
  /** `sub_question` / `per_option_sub` children, in ordinal order. */
  children: IUsageNode[];
  /** `repeat_member` children — rendered per row, not inline. */
  members: IUsageNode[];
}

export interface ISectionView {
  section: ISnapshotSection;
  roots: IUsageNode[];
}

export interface IDefinitionView {
  sections: ISectionView[];
  usagesByCode: Map<string, ISnapshotUsage>;
  /** Option code → label, per usage; the review panel reads answers through it. */
  optionLabels: Map<string, Map<string, string>>;
}

const REPEAT_MEMBER = 'repeat_member';

export function buildDefinitionView(snapshot: IDefinitionSnapshot): IDefinitionView {
  const usagesByCode = new Map(snapshot.usages.map((usage) => [usage.code, usage]));

  const nodes = new Map<string, IUsageNode>(
    snapshot.usages.map((usage) => [usage.code, { usage, children: [], members: [] }])
  );

  const byOrdinal = (a: IUsageNode, b: IUsageNode) => a.usage.ordinal - b.usage.ordinal;
  const roots: IUsageNode[] = [];

  for (const node of nodes.values()) {
    // A breakout's sub-block (SR-416, GA §6) is a child of its group on the wire, beside the
    // category it breaks down and under the same label; "the client nests the sub-block under
    // the category it names" — `percent_group.parent`. Only the tree moves: its addresses, its
    // answers and its finding are the group's child's as sent.
    const breakdownOf =
      node.usage.params?.percent_group?.sum_rule === 'eq_parent'
        ? node.usage.params.percent_group.parent
        : undefined;
    const parentCode =
      breakdownOf && nodes.has(breakdownOf) ? breakdownOf : node.usage.parentUsage;
    // A child whose parent is missing from the snapshot cannot happen against a
    // published version, but dropping it silently would hide a question — it is
    // rendered at the top level instead.
    const parent = parentCode ? nodes.get(parentCode) : undefined;

    if (!parent) {
      roots.push(node);
    } else if (node.usage.childRole === REPEAT_MEMBER) {
      parent.members.push(node);
    } else {
      parent.children.push(node);
    }
  }

  for (const node of nodes.values()) {
    node.children.sort(byOrdinal);
    node.members.sort(byOrdinal);
  }

  const sections = [...snapshot.sections]
    .sort((a, b) => a.ordinal - b.ordinal)
    .map<ISectionView>((section) => ({
      section,
      roots: roots.filter((node) => node.usage.section === section.code).sort(byOrdinal)
    }));

  const optionLabels = new Map<string, Map<string, string>>();
  for (const usage of snapshot.usages) {
    if (!usage.optionList) continue;
    optionLabels.set(
      usage.code,
      new Map(usage.optionList.options.map((option) => [option.code, option.label]))
    );
  }

  return { sections, usagesByCode, optionLabels };
}

/**
 * Whether a section has anything to show. A section whose every usage is hidden
 * is skipped rather than rendered empty — and `sectionVisibility` can hide the
 * whole thing on its own.
 *
 * Roots only, and roots are never repeat members, so no instance is in scope here: a
 * repeat group's own visibility is a group-level fact, and a section holding one is
 * shown whenever the group is, whatever any row inside it gates.
 */
export function isSectionVisible(view: ISectionView, render: IRenderState): boolean {
  if (render.sectionVisibility[view.section.code] === false) return false;
  return view.roots.some((node) => isUsageVisible(node.usage.code, render));
}

/**
 * Visibility is read off a render — survey-api's, or the form's prediction of it
 * (`IUseResponse.render`) — and never derived here. The default when a
 * usage is missing from the map is "show it": the engine reports every usage of
 * the compiled definition, so an absent key means the client and the server
 * disagree about the definition, and hiding a question would be the worse failure.
 *
 * `instanceId` names the repeat row being rendered, and is what makes a member gated on a
 * sibling's answer — `RIA-PC-003`, the CRD number, on `RIA-PC-002` — appear in the rows
 * that opened it and only those. The flat map cannot answer that: for a usage under a
 * repeat it is an OR over the rows (`IRenderState`), so reading it inside a row would
 * show the field in every row as soon as any row opened it. The per-row entry therefore
 * wins wherever the engine emitted one, and everything else — every usage outside a
 * repeat, a repeat group itself, a group with no active rows — falls through to the flat
 * map, which is the whole of what the engine emits for them.
 *
 * A row with no entry in a map that *does* carry its usage cannot happen, which is why the
 * fall-through below is a version fallback and not a hole: survey-api builds the per-usage
 * dictionary as `rows.ToDictionary(...)` over every active instance
 * (`EvaluationContext.BuildResult`), so the entry set is total over active rows rather than
 * limited to the rows some rule fired in. Were that ever to change, this fall-through would
 * silently answer with the OR — the one thing the per-row read exists to avoid — so a
 * survey-api change there obliges a change here.
 *
 * The optional chain reaches the *map*, not just the usage inside it, which is one more
 * `?.` than the rest of this file spends on a contract field. It buys the one degradation
 * worth having: a survey-api older than SR-113 omits both maps entirely, and an unguarded
 * read would throw inside render and blank the form rather than fall back to the flat
 * behaviour that was correct against that API. Not hypothetical — a locally built API
 * image is exactly as old as the last time someone rebuilt it.
 */
export function isUsageVisible(
  usageCode: string,
  render: IRenderState,
  instanceId?: Nullable<string>
): boolean {
  const inRow = instanceId
    ? render.usageVisibilityByInstance?.[usageCode]?.[instanceId]
    : undefined;
  if (inRow !== undefined) return inRow;
  return render.usageVisibility[usageCode] !== false;
}

/** Requiredness, per row and then flat, on exactly the rule `isUsageVisible` documents. */
export function isUsageRequired(
  usageCode: string,
  render: IRenderState,
  instanceId?: Nullable<string>
): boolean {
  const inRow = instanceId
    ? render.requiredByInstance?.[usageCode]?.[instanceId]
    : undefined;
  if (inRow !== undefined) return inRow;
  return render.required[usageCode] === true;
}

/**
 * Whether a per-option sub-question's trigger is currently selected.
 *
 * **This compensates for a gap in survey-api, and is the one visibility rule
 * this app decides for itself.** A `per_option_sub` with a `triggerOptionCode`
 * is meant to appear only when the parent's answer carries that option — the
 * definition builder says so outright ("the engine derives its visibility from
 * the parent's selected option — no rule needed"). The evaluator does not:
 * `EvaluationContext.IsRenderable` walks visibility_default, path, section and
 * rule state but never reads `trigger_option_code`, so every "Other, please
 * specify" box comes back `usageVisibility: true` with nothing selected.
 *
 * Deciding it here is safe in the direction that matters: the gate is a pure
 * function of the parent's stored answer and the definition, so it can only
 * hide a box whose trigger is not selected. Remove this once the engine gates
 * per-option subs itself.
 */
export function isChildTriggered(
  usage: ISnapshotUsage,
  parentSelection: readonly string[]
): boolean {
  if (usage.childRole !== 'per_option_sub') return true;
  // A null trigger once meant "one answer per selected option of the parent", keyed by
  // option code — a different shape, not a gate. survey-api **SR-119** rejects that shape
  // outright, at publish (`AncestryCheck`, `child_role_shape`) and again at compile, so no
  // definition can carry one any more and `answerInstanceKey` below is total. This guard
  // stays as the boundary check it now is: a snapshot that somehow holds one renders the
  // field rather than hiding it on a trigger that is not there to match.
  if (!usage.triggerOptionCode) return true;
  return parentSelection.includes(usage.triggerOptionCode);
}

/** The option codes an answer holds, for the gate above. */
export function selectedCodes(answer: IAnswer | undefined): string[] {
  return answer?.value?.t === 'opts' ? answer.value.v : [];
}

/** `set_text` overrides win over the definition's label (RE §3). */
export function effectiveLabel(usage: ISnapshotUsage, render: IRenderState): string {
  return render.textOverrides[usage.code] ?? usage.label;
}

/**
 * The instanceKey a usage's own answer and draft are addressed at — and the same
 * instanceKey its rendered control's `data-address` carries, since `FormControls.at()`
 * (the E2E suite) defaults to this and has to agree with what `response-field.tsx` stamps.
 *
 * It is also the instanceKey the usage's own *finding* carries, and that agreement is
 * survey-api's rather than something this app arranges. The hard constraint underneath is
 * the write address: `AnswerWriteService` refuses a write to a scalar `per_option_sub` whose
 * instanceKey is not its trigger code (`trigger_key`, `AnswerWriteService.cs:127`), so the
 * control has to sit at the trigger code or an answer to it can never be saved at all. Both
 * of survey-api's finding-producing paths then land there too:
 *
 * - **Synthetic checks that read a stored answer** (`SyntheticChecks.cs`'s `CheckExclusive`,
 *   `CheckMaxSelect`, `CheckOptionHidden`) address their finding at the answer record's own
 *   `InstanceId`/`InstanceKey`, which for a scalar per_option_sub *is* the trigger code (the
 *   write constraint above put it there) — a consequence of the write address, not a
 *   re-derivation of it.
 * - **Rule-emitted `validate` findings** agree since survey-api **SR-114**, which resolves
 *   `Evaluator.TargetKey` through `EvaluationContext.AnswerKeyOf` — the same accessor the
 *   synthetic path uses — instead of passing a hardcoded blank instanceKey regardless of
 *   `childRole`. Until then the two disagreed for exactly this shape: a `validate` rule
 *   targeting a triggered per_option_sub (`RIA-PM-010/last_year` on v1, July's `RIA-S1-016`)
 *   rendered its control at the trigger code and its finding at a blank third segment, and
 *   `FieldFor` merged a second lookup at `''` to compensate. That compensation is gone.
 *   Confirmed against the running stack (`e2e/specs/ria/pm-metrics.spec.ts`'s
 *   `RIA-PM-010.last_year` case), not assumed.
 *
 * survey-api ships its own answer address per usage since **SR-120**
 * (`ISnapshotUsage.answerInstanceKey`), which does *not* make this function redundant: that
 * field comes from `AnswerKeyResolver` and is `null` where a usage has no single address,
 * while findings are addressed through the total `AnswerKeyOf`, and the two shapes where
 * they differ are the two below. `e2e/specs/ria/container-address.spec.ts` pins which
 * divergences are intended, so adopting the wire field wholesale fails there rather than
 * silently.
 *
 * A container `per_option_sub` — `percent_group` / `repeat_group`; no v1 survey seeds a
 * triggered one (July's RIA had `RIA-S9-001/growth_sources`) — is asymmetric with the scalar
 * case above for a third, unrelated reason: it holds no answer of its own (its members do), so
 * the write constraint that pins a scalar to its trigger code has nothing to bite on here.
 * `SyntheticChecks.CheckPercentGroup` emits the container's own finding
 * (`engine.percent_group`) at a hardcoded blank instanceKey, so its address has to stay
 * blank rather than follow its scalar sibling above. `UsageNodeView`'s container stamp
 * (`response-section-view.tsx`) is intentionally left as a literal `''` rather than routed
 * through this function — do not "fix" that into symmetry with the scalar case; the
 * container half is what survey-api actually does, not an oversight this function forgot
 * to cover.
 *
 * A `grid` / `multi_year_table` is excluded for the same reason as a container, not the
 * scalar one: it holds no answer at its own address either — its rows do, keyed by row
 * option code, and `FieldFor` looks the usage's own `engine.required` finding up at `''`
 * — so a triggered grid (RIA seeds none, but this is shared rendering code for every
 * survey) would otherwise get a `data-address` at the trigger code while its answer, its
 * rows and its finding all stay elsewhere. `isCellsField` is the same predicate
 * `response-section-view.tsx` already uses to single out this shape.
 */
export function answerInstanceKey(usage: ISnapshotUsage): string {
  if (CONTAINER_FIELD_TYPES.has(usage.fieldType) || isCellsField(usage.fieldType)) return '';
  return usage.childRole === 'per_option_sub' && usage.triggerOptionCode
    ? usage.triggerOptionCode
    : '';
}

/**
 * The address every answer, finding and write is keyed by.
 *
 * `domAddress`, just below, formats this same triple for the DOM — editing this join or
 * its argument order obliges the same edit there, and in `e2e/pages/response-form.page.ts`,
 * which calls `domAddress` rather than re-deriving it for exactly this reason.
 */
export function addressKey(
  usage: string,
  instanceId: Nullable<string>,
  instanceKey: string
): string {
  return `${usage} ${instanceId ?? ''} ${instanceKey}`;
}

/** `addressKey` read back into its three parts. */
export function addressOf(key: string): {
  usage: string;
  instanceId: Nullable<string>;
  instanceKey: string;
} {
  const [usage, instanceId, instanceKey] = key.split('\u0000');
  return { usage, instanceId: instanceId || null, instanceKey: instanceKey ?? '' };
}

/**
 * The same triple as `addressKey`, in a form an HTML attribute can carry — the hook the
 * E2E suite addresses a question by.
 *
 * Not `addressKey` itself: it joins with NUL, which is unusable here twice over. The HTML
 * parser rewrites U+0000 to U+FFFD, so the attribute would not survive being written, and
 * no CSS or Playwright attribute selector can name it. `|` is safe because the three parts
 * are a usage code, a GUID and an option code, none of which can contain it.
 *
 * Two formatters for one address is a smell, kept adjacent so a change to either is a
 * change to both in the same diff. Addressing by label was the alternative, and it fails
 * on the survey workbooks still being DRAFT and on grid columns that render only a code.
 */
export function domAddress(
  usage: string,
  instanceId: Nullable<string>,
  instanceKey: string
): string {
  return `${usage}|${instanceId ?? ''}|${instanceKey}`;
}

export function indexAnswers(answers: IAnswer[]): Map<string, IAnswer> {
  return new Map(
    answers.map((answer) => [
      addressKey(answer.usage, answer.instanceId, answer.instanceKey),
      answer
    ])
  );
}

/** Findings can share an address, so this is a multimap, unlike answers. */
export function indexFindings(findings: IFinding[]): Map<string, IFinding[]> {
  const byAddress = new Map<string, IFinding[]>();

  for (const finding of findings) {
    if (!finding.usage) continue;
    const key = addressKey(finding.usage, finding.instanceId, finding.instanceKey);
    byAddress.set(key, [...(byAddress.get(key) ?? []), finding]);
  }

  return byAddress;
}

/**
 * The findings worth showing right now (G5, the survey review plan's D4).
 *
 * Every finding here is the server's, and none is discarded — the step gate and the submit gate
 * read them all. What this holds back is every finding the respondent has not reached yet: a
 * fresh draft comes back with a hundred, and a form flagged red before anything is typed says
 * nothing about what to do first. A finding shows once the respondent has finished with its
 * address — left it, or picked it — or, for a container or a table, with an address inside it;
 * once a forward press was made on its step (`revealedUsages`); and after a submit (`reveal`). A
 * finding addressed to no question has no field to wait for, and shows.
 *
 * Rejected: holding back `engine.required` alone, which flagged a step and its fields for every
 * rule a blank draft already fails (the ranking minimum, a percent group) before one answer.
 */
export function visibleFindings(
  findings: IFinding[],
  options: {
    reveal: boolean;
    touched: ReadonlySet<string>;
    /** The usages of the steps a forward press was made on (`sectionUsageCodes`). */
    revealedUsages: ReadonlySet<string>;
    /** A usage's parent (`parentUsage`), to find the containers a touched address sits in. */
    parentOf: (usage: string) => Nullable<string>;
  }
): IFinding[] {
  if (options.reveal) return findings;

  const within = containersTouched(options.touched, options.parentOf);
  return findings.filter((finding) => {
    if (!finding.usage) return true;
    if (options.revealedUsages.has(finding.usage)) return true;
    const key = addressKey(finding.usage, finding.instanceId, finding.instanceKey);
    return options.touched.has(key) || within.has(key);
  });
}

/**
 * The addresses of every table and container holding a touched address: a table's own (key
 * `''`) above a touched row or cell, and each ancestor's above that — at the row's instance and
 * at none, since a repeat group's own findings sit outside its rows.
 */
function containersTouched(
  touched: ReadonlySet<string>,
  parentOf: (usage: string) => Nullable<string>
): Set<string> {
  const within = new Set<string>();
  for (const key of touched) {
    const { usage, instanceId, instanceKey } = addressOf(key);
    if (instanceKey) within.add(addressKey(usage, instanceId, ''));
    for (let parent = parentOf(usage); parent; parent = parentOf(parent)) {
      within.add(addressKey(parent, instanceId, ''));
      within.add(addressKey(parent, null, ''));
    }
  }
  return within;
}

/**
 * What a finding should read as.
 *
 * Authored `validate` messages are the survey's own words and pass through untouched.
 * The engine's two synthesized codes are the exception, and for the same reason in both
 * cases: their messages are addressed to whoever is reading an evaluation, not to the
 * advisor filling the form.
 *
 * - `engine.required` says `'RIA-FP-003' is required`. The question's label is in the
 *   snapshot, so it is substituted for the code.
 * - `engine.format` (survey-api SR-122) says only the predicate — `is above 100`, `is
 *   longer than 255 characters` — with no subject at all, because the server composes one
 *   nowhere. A named format has respondent copy of its own, keyed by the constraint's
 *   `messageCode`; a length or bound violation has none, so the label or "This answer"
 *   becomes the missing subject and the engine's own words finish the sentence.
 *
 * A cell-addressed `engine.format` is the one message that *does* carry a subject, and it
 * is spelled in codes (`'TEAM-S8-016' column 'amount' is above 100`). That prefix is
 * dropped by exact match — not by pattern — so a wording change on the server costs a
 * slightly redundant sentence rather than a mangled one, and the column takes its place.
 *
 * A table row's `engine.required` naming a `cell` (SR-416: a row a rule made required, one
 * finding per missing cell) — or several of them said as one by `mergeCellFindings` — reads
 * "needs <column words>" under the row, and "<question>, <row> — needs …" in a panel; the
 * row is the grid's option label, or a year row's head (`multiYearRows`). A column is named by
 * its words (`columnWords`), so a cell's `engine.format` names them too.
 *
 * The resolver takes the whole usage rather than its label, because `engine.format` needs
 * the constraint too; one that returned only what today's caller happens to need would
 * have to be widened every time the copy is.
 *
 * `where` is what the two synthesized codes need and the authored ones do not: **which
 * subject to supply**. Under the control the question's label is already on screen a line
 * above, so naming it again stutters — `engine.required` drops it entirely and
 * `engine.format`, which cannot drop a subject and still be a sentence, says "This
 * answer". In a panel the finding is all there is, so both name the question. One
 * parameter rather than two functions, because it is one decision, and a second
 * implementation of "what does a finding read as" is how the two would drift.
 */
export function findingText(
  finding: IShownFinding,
  usageOf?: (usageCode: string) => ISnapshotUsage | undefined,
  where: 'panel' | 'inline' = 'panel',
  yearLabels?: Record<string, string>
): string {
  const usage = finding.usage ? usageOf?.(finding.usage) : undefined;

  if (finding.code === 'engine.required') {
    const cells = finding.cells ?? (finding.cell ? [finding.cell] : null);
    if (cells) {
      const needs = `needs ${cells.map((cell) => columnWords(usage, cell)).join(', ')}`;
      if (where === 'inline' || !usage) return needs;
      const row =
        [...gridRows(usage), ...multiYearRows(usage, yearLabels)].find(
          (candidate) => candidate.key === finding.instanceKey
        )?.label ?? finding.instanceKey;
      return `${usage.label}, ${row} — ${needs}`;
    }
    const label = where === 'inline' ? undefined : usage?.label;
    return label ? `${label} — an answer is required` : 'An answer is required';
  }

  if (finding.code === 'engine.format') {
    if (finding.cell) {
      const prefix = `'${finding.usage}' column '${finding.cell}' `;
      return finding.message.startsWith(prefix)
        ? `${columnWords(usage, finding.cell)} ${finding.message.slice(prefix.length)}`
        : finding.message;
    }
    // Format copy first: it is a whole sentence written for a respondent, where the
    // engine's fragment is a predicate written for a log.
    const copy = formatMessage(usage?.constraint?.messageCode ?? null);
    if (copy) return copy;
    // A date past its latest day (SR-416, an age bound): the engine says `is after <maxDate>`,
    // the day in ISO or in the `M/d/yyyy` its `{{as_of_date}}` renders, matched exactly — any
    // other wording passes through as below.
    const maxDate = usage?.constraint?.maxDate;
    if (
      maxDate &&
      (finding.message === `is after ${maxDate}` ||
        finding.message === `is after ${numericDay(maxDate)}`)
    ) {
      return `Enter a date on or before ${spelledDay(maxDate)}.`;
    }
    const subject = where === 'inline' ? 'This answer' : usage?.label;
    return subject ? `${subject} ${finding.message}` : finding.message;
  }

  if (finding.code === PERCENT_GROUP) {
    const words = percentGroupWords(finding, usage, usageOf, where);
    if (words) return words;
  }

  if (finding.code === UNCONFIRMED) {
    const words = unconfirmedWords(finding, usage, where);
    if (words) return words;
  }

  return finding.message;
}

const PERCENT_GROUP = 'engine.percent_group';
const UNCONFIRMED = 'engine.unconfirmed_proposal';

/**
 * A registry's answer still unconfirmed, in the form's words (the survey review plan's D5). The
 * engine says `'<usage>' holds a value from <source> that is not confirmed yet`, the source by its
 * code; the sentence is matched **exactly** and the source named as the field's badge names it
 * (`sourceCopy`) — any other wording passes through.
 */
function unconfirmedWords(
  finding: IShownFinding,
  usage: ISnapshotUsage | undefined,
  where: 'panel' | 'inline'
): Nullable<string> {
  const prefix = `'${finding.usage}' holds a value from `;
  const suffix = ' that is not confirmed yet';
  const { message } = finding;
  if (!message.startsWith(prefix) || !message.endsWith(suffix)) return null;
  const source = message.slice(prefix.length, message.length - suffix.length);
  const named = sourceCopy(source).label;
  const ask = named.startsWith('From ')
    ? `confirm or change the answer from ${named.slice('From '.length)}.`
    : 'confirm or change this answer.';
  return where === 'inline' || !usage
    ? `${ask[0].toUpperCase()}${ask.slice(1)}`
    : `${usage.label} — ${ask}`;
}

/**
 * A percent group's sum, in the form's words (SR-416, B7-3). The engine says it in codes —
 * `'FA-IA-002' members must sum to 100`, and for a breakout's sub-block `'<sub-block>' members
 * must sum to '<category>'` — so each of its two sentences is matched **exactly**, the codes put
 * back as the questions' labels; any other wording passes through, a redundant sentence rather
 * than a mangled one. A category's label carries its unit ("Equities (%)"), which a sentence about
 * the share does not repeat.
 *
 * Under the group (`inline`) its question is on screen a line above, so it is not named again;
 * in a panel or the step's dialog the finding is all there is.
 */
function percentGroupWords(
  finding: IShownFinding,
  usage: ISnapshotUsage | undefined,
  usageOf: ((usageCode: string) => ISnapshotUsage | undefined) | undefined,
  where: 'panel' | 'inline'
): Nullable<string> {
  const rule = usage?.params?.percent_group;
  if (!usage || !rule) return null;
  if (rule.sum_rule === 'eq_100' && finding.message === `'${usage.code}' members must sum to 100`) {
    return where === 'inline'
      ? 'The answers must add up to 100%.'
      : `${usage.label} — the answers must add up to 100%.`;
  }
  const parent = rule.parent ? usageOf?.(rule.parent) : undefined;
  if (
    rule.sum_rule === 'eq_parent' &&
    parent &&
    finding.message === `'${usage.code}' members must sum to '${parent.code}'`
  ) {
    const share = parent.label.replace(/\s*\(%\)\s*$/, '');
    return `The parts of ${share} must add up to the ${share} share.`;
  }
  return null;
}

/** A `yyyy-MM-dd` day spelled out — "December 31, 2009" — read in UTC both ways, or it is the
 *  evening before west of Greenwich. */
function spelledDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      });
}

/** A `yyyy-MM-dd` day as survey-api renders a date into its words — `12/31/2008`, no padding. */
export function numericDay(day: string): string {
  const [year, month, date] = day.split('-');
  return `${Number(month)}/${Number(date)}/${year}`;
}

/**
 * A finding as the form says it: survey-api's own, or a table row's per-cell `engine.required`
 * findings said as one (`mergeCellFindings`), `cells` naming the missing cells.
 */
export interface IShownFinding extends IFinding {
  /** The missing cells of one row, in the order survey-api sent them — on a merged finding alone,
   *  whose `cell` is then null. */
  cells?: string[];
}

/**
 * Says a row's missing cells once (B4-7): survey-api raises one `engine.required` per missing
 * cell of a row a rule made required (`SyntheticChecks`, SR-416) — up to twelve on a blank
 * FA-PM-001 — and a list naming the row once per cell says nothing the one line would not. Every
 * other finding passes through as it came, in order; the merged one takes the first's place.
 */
export function mergeCellFindings(findings: IFinding[]): IShownFinding[] {
  const shown: IShownFinding[] = [];
  const rows = new Map<string, IShownFinding & { cells: string[] }>();
  for (const finding of findings) {
    if (finding.code !== 'engine.required' || !finding.cell || !finding.usage) {
      shown.push(finding);
      continue;
    }
    const key = [
      finding.usage,
      finding.instanceId ?? '',
      finding.instanceKey,
      finding.severity
    ].join('|');
    const row = rows.get(key);
    if (row) {
      row.cells.push(finding.cell);
      continue;
    }
    const merged = { ...finding, cell: null, cells: [finding.cell] };
    rows.set(key, merged);
    shown.push(merged);
  }
  return shown;
}

/** A table column in words: its `label`, else its code, as the table's own header draws it. */
export function columnWords(usage: ISnapshotUsage | undefined, code: string): string {
  const columns = usage?.params?.grid?.columns ?? usage?.params?.multi_year?.columns ?? [];
  return columns.find((column) => column.code === code)?.label ?? code;
}

/** Findings with no usage — they belong to the response, not to a control. */
export function responseLevelFindings(findings: IFinding[]): IFinding[] {
  return findings.filter((finding) => !finding.usage);
}

/**
 * Active rows of one repeat group, in ordinal order. Tombstoned rows
 * (`excluded` / `removed`) ship in the state so their retained answers stay
 * addressable — they are not rendered as rows.
 */
export function activeRows(repeats: IRepeatRow[], usageCode: string): IRepeatRow[] {
  return repeats
    .filter((row) => row.usage === usageCode && row.status === 'active')
    .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
}

/**
 * Rows a grid renders: one per option of the pinned row list, keyed by option
 * code — which is exactly what `answer.instance_key` holds for a grid.
 */
export function gridRows(usage: ISnapshotUsage): { key: string; label: string }[] {
  return (usage.optionList?.options ?? [])
    .slice()
    .sort((a, b) => a.ordinal - b.ordinal)
    .map((option) => ({ key: option.code, label: option.label }));
}

/**
 * Rows a multi-year table renders: one per offset, keyed `y{offset}` and headed by the table's own
 * words for that year (`row_labels`, survey-api's, tokens expanded), else the cycle's label.
 */
export function multiYearRows(
  usage: ISnapshotUsage,
  yearLabels: Record<string, string> | undefined
): { key: string; label: string }[] {
  return (usage.params?.multi_year?.offsets ?? []).map((offset) => {
    const key = `y${offset}`;
    const words = usage.params?.multi_year?.row_labels?.[key];
    return { key, label: words ?? yearLabels?.[key] ?? key };
  });
}

/**
 * A choice list's options under their headings (SR-416, F18; the owner's Q20 (a)): survey-api
 * keeps RIA-SV-001's five service headings as options — "so the client can group the services
 * under them" (`ServicesBlock.cs`) — each hidden by a rule that never holds and named by a `_HEAD`
 * code. So a hidden `_HEAD` option opens a group headed by its label, the options after it in list
 * order belong to it, and the engine's visibility is still the only thing that decides what is
 * offered. A list with no heading is one group with none. Under headings, `OTHER` is no heading's
 * service: it opens a group of its own, headed by nothing (the survey review's R14).
 */
export function groupedOptions(
  usage: ISnapshotUsage,
  render: IRenderState
): { heading: Nullable<string>; options: ISnapshotOption[] }[] {
  const hidden = render.optionVisibility[usage.code];
  const groups: { heading: Nullable<string>; options: ISnapshotOption[] }[] = [];
  const ordered = (usage.optionList?.options ?? []).slice().sort((a, b) => a.ordinal - b.ordinal);
  for (const option of ordered) {
    const shown = hidden?.[option.code] !== false;
    if (!shown && option.code.endsWith('_HEAD')) {
      groups.push({ heading: option.label, options: [] });
      continue;
    }
    if (!shown) continue;
    const apart = option.code === OTHER && groups.some((group) => group.heading);
    if (groups.length === 0 || apart) groups.push({ heading: null, options: [] });
    groups[groups.length - 1].options.push(option);
  }
  return groups.filter((group) => group.options.length > 0);
}

/** The option code survey-api's lists give "Other". */
const OTHER = 'OTHER';

/** Options the engine has not hidden, in ordinal order. */
export function visibleOptions(usage: ISnapshotUsage, render: IRenderState) {
  const hidden = render.optionVisibility[usage.code];
  return (usage.optionList?.options ?? [])
    .slice()
    .sort((a, b) => a.ordinal - b.ordinal)
    .filter((option) => hidden?.[option.code] !== false);
}
